import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { PurchaseBillPayload, SelectOption } from "../types";

interface PurchaseBillItemsTableProps {
  spareParts: SelectOption[];
}

const emptyPurchaseBillItem = () => ({
  sparePartId: "",
  quantity: 1,
  unitPrice: 0,
  remarks: "",
});

const PurchaseBillItemsTable = ({ spareParts }: PurchaseBillItemsTableProps) => {
  const { control, watch } = useFormContext<PurchaseBillPayload>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const items = watch("items") || [];

  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => append(emptyPurchaseBillItem())}
        >
          Add Item
        </Button>
      </Stack>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Spare Part</TableCell>
            <TableCell>Qty</TableCell>
            <TableCell>Unit Price</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Remarks</TableCell>
            <TableCell align="center">Action</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {fields.map((field, index) => {
            const qty = Number(items[index]?.quantity || 0);
            const price = Number(items[index]?.unitPrice || 0);
            const total = qty * price;

            return (
              <TableRow key={field.id}>
                <TableCell width="30%">
                  <Controller
                    control={control}
                    name={`items.${index}.sparePartId`}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        onChange={(event) => {
                          field.onChange(event);

                          // Keep one ready-to-fill row after the last selected spare.
                          if (event.target.value && index === fields.length - 1) {
                            append(emptyPurchaseBillItem());
                          }
                        }}
                      >
                        {spareParts.map((part) => (
                          <MenuItem key={part.id} value={part.id}>
                            {part.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </TableCell>

                <TableCell>
                  <Controller
                    control={control}
                    name={`items.${index}.quantity`}
                    render={({ field }) => (
                      <TextField {...field} type="number" size="small" sx={{ width: 90 }} />
                    )}
                  />
                </TableCell>

                <TableCell>
                  <Controller
                    control={control}
                    name={`items.${index}.unitPrice`}
                    render={({ field }) => (
                      <TextField {...field} type="number" size="small" sx={{ width: 130 }} />
                    )}
                  />
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>{total.toFixed(2)}</TableCell>

                <TableCell>
                  <Controller
                    control={control}
                    name={`items.${index}.remarks`}
                    render={({ field }) => (
                      <TextField {...field} size="small" sx={{ minWidth: 160 }} />
                    )}
                  />
                </TableCell>

                <TableCell align="center">
                  <IconButton color="error" onClick={() => remove(index)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default PurchaseBillItemsTable;
